import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { api } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Trash, Minus, Plus, ArrowRight, ArrowSquareOut, CheckCircle, Lock } from "@phosphor-icons/react";
import { formatPrice } from "@/lib/api";
import { toast } from "sonner";

const CART_STORAGE_KEY = "jl88_cart";

export default function CartSheet() {
  const navigate = useNavigate();
  const { open, setOpen, items, remove, setQty, totals, clear } = useCart();
  const { printify_store_url } = useSiteSettings();
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  // If PayPal is configured we take the shopper through our own /checkout flow;
  // otherwise we gracefully fall back to the legacy Printify-store redirect so
  // the site never has a dead checkout button.
  const [onsiteCheckout, setOnsiteCheckout] = useState(false);

  useEffect(() => {
    api.get("/checkout/config")
      .then((r) => setOnsiteCheckout(!!r.data?.configured))
      .catch(() => setOnsiteCheckout(false));
  }, []);

  function handleCheckout() {
    if (items.length === 0) return;

    // Snapshot — belt-and-braces so a browser quirk during redirect can never
    // wipe the shopper's selection.
    try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items)); } catch (e) { console.warn("Browser storage unavailable", e); }

    // Preferred: in-site checkout once PayPal is live.
    if (onsiteCheckout) {
      setOpen(false);
      navigate("/checkout");
      return;
    }

    // Fallback: current Printify-store redirect behavior.
    const perProductUrls = Array.from(new Set(items.map((i) => i.checkout_url).filter(Boolean)));
    if (items.length === 1 && perProductUrls.length === 1) {
      window.open(perProductUrls[0], "_blank", "noopener,noreferrer");
      setCheckoutStarted(true);
      return;
    }
    if (printify_store_url) {
      const skippedCount = items.filter((i) => !i.checkout_url).length;
      toast("Opening the full Printify store", {
        description:
          items.length > 1
            ? "Printify checks out one listing at a time — grab each item from the storefront to pay."
            : skippedCount > 0
            ? "No direct link set for this item — pick it up on the storefront."
            : undefined,
      });
      window.open(printify_store_url, "_blank", "noopener,noreferrer");
      setCheckoutStarted(true);
      return;
    }
    toast.error("Checkout not configured yet", {
      description: "Add your Printify store link in the admin Sync tab so shoppers can check out.",
    });
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-sheet" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Your Tackle Box</SheetTitle>
          <SheetDescription className="sr-only">Review the items in your cart and continue to checkout.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-6 space-y-4">
          {items.length === 0 && (
            <p data-testid="cart-empty" className="text-sm text-muted-foreground">
              Your cart is empty. Go hook something good.
            </p>
          )}
          {items.map((i) => (
            <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-3 border border-border p-3 rounded-sm">
              {i.image && <img src={i.image} alt={i.title} className="w-20 h-20 object-cover rounded-sm" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm truncate">{i.title}</div>
                {i.variant_title && <div className="text-xs text-muted-foreground">{i.variant_title}</div>}
                <div className="text-sm text-[#C84A31] mt-1">{formatPrice(i.price)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    data-testid={`qty-dec-${i.key}`}
                    onClick={() => setQty(i.key, i.qty - 1)}
                    className="p-1 border border-border rounded-sm hover:bg-muted transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="text-sm w-6 text-center">{i.qty}</span>
                  <button
                    data-testid={`qty-inc-${i.key}`}
                    onClick={() => setQty(i.key, i.qty + 1)}
                    className="p-1 border border-border rounded-sm hover:bg-muted transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    data-testid={`remove-${i.key}`}
                    onClick={() => remove(i.key)}
                    className="ml-auto p-1 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <SheetFooter className="border-t border-border pt-4 flex-col gap-3 sm:flex-col">
          <div className="w-full flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-display text-lg">{formatPrice(totals.subtotal)}</span>
          </div>

          {/* Reassurance banner — only after the shopper has clicked Checkout
              and been redirected to Printify in a new tab. Reminds them their
              tackle box is saved on OUR side, so they can swing back any time. */}
          {checkoutStarted && items.length > 0 && (
            <div
              data-testid="cart-saved-banner"
              className="w-full flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-sm p-3"
            >
              <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs leading-relaxed text-emerald-900">
                <strong className="font-display">Your tackle box is saved.</strong>
                {" "}Finish your order on the Printify tab — these items will still be here when you come back.
              </div>
            </div>
          )}

          <Button
            data-testid="checkout-btn"
            disabled={items.length === 0}
            onClick={handleCheckout}
            className="w-full btn-primary rounded-sm h-11 font-display tracking-wide"
          >
            {onsiteCheckout
              ? "Checkout"
              : checkoutStarted
              ? "Reopen Printify"
              : "Checkout on Printify"}
            {onsiteCheckout
              ? <ArrowRight size={16} className="ml-2" />
              : <ArrowSquareOut size={16} className="ml-2" />}
          </Button>

          {items.length > 0 && (
            <div className="flex items-center justify-between gap-3 pt-1">
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Lock size={11} weight="fill" /> Cart saved on this device
              </span>
              <button
                onClick={() => { clear(); setCheckoutStarted(false); }}
                data-testid="cart-clear"
                className="text-xs text-muted-foreground hover:text-destructive underline underline-offset-4"
              >
                Clear cart
              </button>
            </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
